import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Separator } from '@vaiu/ui/separator';
import { Badge } from '@vaiu/ui/badge';
import { 
  ArrowLeft, 
  Shield, 
  Mail, 
  FileText, 
  User, 
  Lock,
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  Calendar,
  Globe,
  CreditCard,
  Users,
  Building2,
  Gavel,
  DollarSign,
  Smartphone,
  QrCode,
  Zap,
  Clock,
  Ban,
  Scale
} from 'lucide-react';

const TermsOfServicePage: React.FC = () => {
  const lastUpdated = "July 2025";
  const effectiveDate = "January 1, 2025";

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="mb-8">
        <Link to="/">
          <Button variant="ghost" className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </Link>
        <div className="flex items-center space-x-3 mb-4">
          <Gavel className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Terms of Service</h1>
            <p className="text-gray-600">
              Last updated: {lastUpdated} | Effective: {effectiveDate}
            </p>
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-blue-800">
            Welcome to the Vaiu Commerce Shopping Experience Network. These Terms of Service ("Terms") govern your use 
            of our technology platform and services provided by Vaiu Global Inc.
          </p>
        </div>
      </div>

      {/* Important Disclaimer */}
      <div className="mb-8">
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="text-orange-900 flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5" />
              <span>Important Legal Notice</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="text-orange-800 space-y-2">
            <p className="font-medium">
              Vaiu Global Inc. is a technology company, not a bank or financial institution.
            </p>
            <ul className="text-sm space-y-1">
              <li>• We do not hold, store, or custody your funds</li>
              <li>• All financial services are provided by licensed third-party partners</li>
              <li>• Our tokens represent technology credits, not stored value instruments</li>
              <li>• We facilitate transactions but do not conduct money transmission</li>
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Quick Navigation */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="w-5 h-5 text-blue-600" />
            <span>Table of Contents</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <a href="#acceptance" className="block text-blue-600 hover:text-blue-800 text-sm">1. Acceptance of Terms</a>
              <a href="#platform-description" className="block text-blue-600 hover:text-blue-800 text-sm">2. Platform Description</a>
              <a href="#eligibility" className="block text-blue-600 hover:text-blue-800 text-sm">3. Eligibility</a>
              <a href="#account-registration" className="block text-blue-600 hover:text-blue-800 text-sm">4. Account Registration</a>
            </div>
            <div className="space-y-2">
              <a href="#token-services" className="block text-blue-600 hover:text-blue-800 text-sm">5. Token Services</a>
              <a href="#payment-processing" className="block text-blue-600 hover:text-blue-800 text-sm">6. Payment Processing</a>
              <a href="#fees" className="block text-blue-600 hover:text-blue-800 text-sm">7. Fees & Charges</a>
              <a href="#merchant-services" className="block text-blue-600 hover:text-blue-800 text-sm">8. Merchant Services</a>
            </div>
            <div className="space-y-2">
              <a href="#prohibited-activities" className="block text-blue-600 hover:text-blue-800 text-sm">9. Prohibited Activities</a>
              <a href="#compliance" className="block text-blue-600 hover:text-blue-800 text-sm">10. Compliance</a>
              <a href="#limitations" className="block text-blue-600 hover:text-blue-800 text-sm">11. Limitations</a>
              <a href="#contact" className="block text-blue-600 hover:text-blue-800 text-sm">12. Contact Information</a>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 1. Acceptance of Terms */}
      <section id="acceptance" className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">1. Acceptance of Terms</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700">
              By accessing or using the Vaiu Commerce Shopping Experience Network ("Platform"), you agree to be bound by 
              these Terms of Service. If you do not agree to these terms, you may not use our services.
            </p>
            
            <div className="space-y-3">
              <h3 className="font-semibold">What This Agreement Covers</h3>
              <ul className="text-gray-700 space-y-2">
                <li>• Your use of our technology platform and mobile applications</li>
                <li>• Token creation, management, and redemption services</li>
                <li>• Merchant profile and business services</li>
                <li>• QR code payment and transfer functionalities</li>
                <li>• API access and integration services</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">Changes to Terms</h3>
              <p className="text-gray-700 text-sm">
                We may modify these Terms at any time. We will notify you of material changes via email or 
                in-app notification. Continued use of the Platform after changes constitutes acceptance of the new Terms.
              </p>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* 2. Platform Description */}
      <section id="platform-description" className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">2. Platform Description</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-semibold mb-2 flex items-center space-x-2">
                <Zap className="w-5 h-5 text-blue-600" />
                <span>Technology Platform</span>
              </h3>
              <p className="text-gray-700 text-sm">
                Vaiu Global Inc. operates a technology platform that facilitates digital commerce through tokenized 
                payment systems. We provide software, APIs, and network infrastructure that connects users, merchants, 
                and licensed financial service providers.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-3 flex items-center space-x-2">
                  <QrCode className="w-5 h-5 text-green-600" />
                  <span>Core Services</span>
                </h3>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Digital token creation and management</li>
                  <li>• QR code payment solutions</li>
                  <li>• Peer-to-peer payment facilitation</li>
                  <li>• Merchant payment acceptance tools</li>
                  <li>• Multi-currency transaction support</li>
                  <li>• Business profile management</li>
                </ul>
              </div>

              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-3 flex items-center space-x-2">
                  <Building2 className="w-5 h-5 text-purple-600" />
                  <span>Technology Infrastructure</span>
                </h3>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Web and mobile applications</li>
                  <li>• RESTful API endpoints</li>
                  <li>• Real-time notification systems</li>
                  <li>• Security and fraud detection</li>
                  <li>• Analytics and reporting tools</li>
                  <li>• White-label solutions</li>
                </ul>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="font-medium text-yellow-900 mb-2 flex items-center space-x-2">
                <Shield className="w-4 h-4" />
                <span>What We Are NOT</span>
              </h3>
              <ul className="text-yellow-800 text-sm space-y-1">
                <li>• We are not a bank, credit union, or financial institution</li>
                <li>• We do not hold, custody, or store customer funds</li>
                <li>• We are not a money transmitter or payment processor</li>
                <li>• We do not issue stored value instruments or e-money</li>
                <li>• We are not regulated as a financial services provider</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* 3. Eligibility */}
      <section id="eligibility" className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">3. Eligibility Requirements</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700">
              To use our Platform, you must meet the following requirements:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <h3 className="font-semibold">Individual Users</h3>
                <ul className="text-gray-700 text-sm space-y-1">
                  <li>• Be at least 18 years of age</li>
                  <li>• Have legal capacity to enter contracts</li>
                  <li>• Provide accurate identification information</li>
                  <li>• Reside in a supported jurisdiction</li>
                  <li>• Have a valid email address and phone number</li>
                </ul>
              </div>
              
              <div className="space-y-3">
                <h3 className="font-semibold">Business Users</h3>
                <ul className="text-gray-700 text-sm space-y-1">
                  <li>• Be a legally registered business entity</li>
                  <li>• Have authority to bind the business</li>
                  <li>• Provide business registration documents</li>
                  <li>• Maintain valid business licenses</li>
                  <li>• Comply with applicable regulations</li>
                </ul>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-medium text-red-900 mb-2">Prohibited Jurisdictions</h3>
              <p className="text-red-800 text-sm">
                Our services may not be available in all jurisdictions. You may not use the Platform if you are located 
                in, or a resident of, any jurisdiction where such use would violate applicable law or regulation.
              </p>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* 4. Account Registration */}
      <section id="account-registration" className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">4. Account Registration & Security</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            
            <div className="space-y-4">
              <div className="border-l-4 border-green-500 pl-4">
                <h3 className="font-semibold mb-2">Registration Process</h3>
                <ul className="text-gray-700 text-sm space-y-1">
                  <li>• Provide accurate and complete registration information</li>
                  <li>• Verify your email address and phone number</li>
                  <li>• Create secure login credentials</li>
                  <li>• Complete identity verification if required</li>
                  <li>• Accept these Terms of Service and Privacy Policy</li>
                </ul>
              </div>
              
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="font-semibold mb-2">Account Security</h3>
                <ul className="text-gray-700 text-sm space-y-1">
                  <li>• You are responsible for maintaining account security</li>
                  <li>• Use strong, unique passwords and enable two-factor authentication</li>
                  <li>• Do not share your login credentials with others</li>
                  <li>• Notify us immediately of any unauthorized access</li>
                  <li>• Log out from shared or public devices</li>
                </ul>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">Account Suspension or Termination</h3>
              <p className="text-gray-700 text-sm">
                We may suspend or terminate your account for violations of these Terms, suspicious activity, 
                regulatory requirements, or other legitimate business reasons. We will provide notice when 
                possible and assist with data export where appropriate.
              </p>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* 5. Token Services */}
      <section id="token-services" className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">5. Token Services</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-2">What Are Vaiu Tokens?</h3>
              <p className="text-blue-800 text-sm">
                Vaiu tokens are digital technology credits that represent your authorization to access value through our 
                licensed financial partners. Tokens are NOT stored value instruments, e-money, or digital currency.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-3">Token Types</h3>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li>• <strong>Cash Tokens:</strong> General payment tokens</li>
                  <li>• <strong>Invoice Tokens:</strong> Business payment requests</li>
                  <li>• <strong>QR Code Tokens:</strong> Scannable payment codes</li>
                  <li>• <strong>Gift Tokens:</strong> Redeemable gift certificates</li>
                </ul>
              </div>

              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-3">Token Features</h3>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li>• Multi-currency support (USD, EUR, GBP, BTC)</li>
                  <li>• Customizable expiration dates</li>
                  <li>• Message and description fields</li>
                  <li>• Real-time status tracking</li>
                </ul>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold">Token Lifecycle</h3>
              
              <div className="border-l-4 border-green-500 pl-4">
                <h4 className="font-medium mb-1">Creation</h4>
                <p className="text-gray-700 text-sm">
                  When you create a token, you authorize us to facilitate a future payment through our licensed partners. 
                  No funds are held by Vaiu Global during this process.
                </p>
              </div>
              
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-medium mb-1">Redemption</h4>
                <p className="text-gray-700 text-sm">
                  When someone redeems your token, we facilitate the actual payment through our licensed financial partners. 
                  The redeemer receives value directly from these partners, not from Vaiu Global.
                </p>
              </div>
              
              <div className="border-l-4 border-gray-500 pl-4">
                <h4 className="font-medium mb-1">Expiration</h4>
                <p className="text-gray-700 text-sm">
                  Tokens expire based on the timeframe you set. Expired tokens cannot be redeemed and may be cancelled. 
                  You may receive refunds through the original payment method via our partners.
                </p>
              </div>

              <div className="border-l-4 border-purple-500 pl-4">
                <h4 className="font-medium mb-1">Escrowed Tokens</h4>
                <p className="text-gray-700 text-sm">
                  Users may create tokens with conditional redemption triggers ("Escrowed Tokens"). Vaiu acts as a neutral
                  technology intermediary to facilitate the release of payment authorization based on verifiable,
                  pre-defined conditions (e.g., delivery confirmation, date-based triggers).
                </p>
                <ul className="text-xs text-gray-600 mt-2 list-disc pl-4 space-y-1">
                  <li>Vaiu Global Inc. is not a licensed escrow agent and does not provide legal escrow services.</li>
                  <li>Funds remain authorized with our licensed financial partners, not held by Vaiu.</li>
                  <li>We are not responsible for resolving disputes between parties regarding escrow conditions.</li>
                </ul>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="font-medium text-yellow-900 mb-2">Important Limitations</h3>
              <ul className="text-yellow-800 text-sm space-y-1">
                <li>• Tokens are not bank deposits or investments</li>
                <li>• Tokens do not earn interest or appreciate in value</li>
                <li>• Token redemption depends on third-party processor availability</li>
                <li>• Some tokens may have geographic or merchant restrictions</li>
                <li>• Token codes should be kept secure and not shared publicly</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* 6. Payment Processing */}
      <section id="payment-processing" className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">6. Payment Processing & Third-Party Services</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            
            <div className="border-l-4 border-red-500 pl-4">
              <h3 className="font-semibold mb-2">Third-Party Financial Services</h3>
              <p className="text-gray-700 text-sm">
                All actual payment processing, money transmission, and financial services are provided by licensed 
                third-party partners. Vaiu Global does not process payments or hold funds.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-3">Payment Partners</h3>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li>• <strong>Stripe, Inc.</strong> - Credit/debit card processing</li>
                  <li>• <strong>PayPal Holdings, Inc.</strong> - Digital wallet payments</li>
                  <li>• <strong>Plaid Inc.</strong> - Bank account connections</li>
                  <li>• <strong>Licensed Banks</strong> - ACH and wire transfers</li>
                  <li>• <strong>Bo Payments</strong> - Virtual prepaid cards</li>
                </ul>
              </div>

              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-3">Supported Methods</h3>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li>• Visa, Mastercard, American Express</li>
                  <li>• PayPal and digital wallets</li>
                  <li>• ACH bank transfers</li>
                  <li>• Bitcoin and other cryptocurrencies (via third-party processors)</li>
                  <li>• Virtual prepaid cards</li>
                </ul>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold">How Payment Processing Works</h3>
              
              <div className="space-y-3">
                <div className="bg-gray-50 rounded p-3">
                  <h4 className="font-medium text-sm">1. Token Creation</h4>
                  <p className="text-xs text-gray-600">
                    You provide payment authorization to our licensed partners through our platform interface.
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded p-3">
                  <h4 className="font-medium text-sm">2. Fund Authorization</h4>
                  <p className="text-xs text-gray-600">
                    Our partners authorize (but do not charge) your selected payment method for the token amount.
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded p-3">
                  <h4 className="font-medium text-sm">3. Token Sharing</h4>
                  <p className="text-xs text-gray-600">
                    You share the token code or QR code with the intended recipient through any communication method.
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded p-3">
                  <h4 className="font-medium text-sm">4. Redemption Processing</h4>
                  <p className="text-xs text-gray-600">
                    When redeemed, our partners process the actual payment and transfer funds to the recipient.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">Cryptocurrency Transactions</h3>
              <p className="text-gray-700 text-sm">
                For transactions involving cryptocurrencies like Bitcoin:
              </p>
              <ul className="text-xs text-gray-600 mt-2 list-disc pl-4 space-y-1">
                <li><strong>Non-Custodial:</strong> Vaiu Global Inc. does not custody, store, or control your crypto assets. All transactions are processed through integrated third-party crypto payment gateways. You maintain full control of your crypto in your own wallet.</li>
                <li><strong>Volatility Risk:</strong> Cryptocurrency values can be highly volatile. We are not responsible for any loss due to market fluctuations.</li>
                <li><strong>Transaction Finality:</strong> Blockchain transactions are irreversible. Ensure your transaction details are correct before broadcasting.</li>
                <li><strong>Network Fees:</strong> You are responsible for any blockchain network fees (e.g., gas fees) required to process a transaction.</li>
              </ul>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h3 className="font-medium text-orange-900 mb-2">Partner Dependencies & Terms</h3>
              <p className="text-orange-800 text-sm">
                Our services depend on third-party financial partners. By using a specific payment method, you agree to
                be bound by the applicable terms of service of that payment partner (e.g., Stripe Services Agreement,
                Plaid End User Privacy Policy). Service availability, processing times, and transaction limits are
                subject to our partners' terms and conditions. We are not responsible for partner service
                interruptions or limitations.
              </p>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* 7. Fees & Charges */}
      <section id="fees" className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl flex items-center space-x-2">
              <DollarSign className="w-5 h-5" />
              <span>7. Fees & Charges</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-2">Fee Structure</h3>
              <p className="text-blue-800 text-sm">
                Our fees are charged for technology services and platform access. Payment processing fees 
                are charged separately by our licensed financial partners.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-3 text-green-600">Consumer Users</h3>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li>• <strong>Token Creation:</strong> Free</li>
                  <li>• <strong>Token Redemption:</strong> Free</li>
                  <li>• <strong>QR Code Generation:</strong> Free</li>
                  <li>• <strong>Basic Features:</strong> Free</li>
                </ul>
                <Badge variant="secondary" className="mt-2 text-xs">No Platform Fees</Badge>
              </div>

              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-3 text-blue-600">Merchant Users</h3>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li>• <strong>Payment Acceptance:</strong> 2.9% + $0.30</li>
                  <li>• <strong>Business Profile:</strong> Free</li>
                  <li>• <strong>QR Code Payments:</strong> Standard rates</li>
                  <li>• <strong>Analytics & Reports:</strong> Free</li>
                </ul>
                <Badge variant="outline" className="mt-2 text-xs">Business Rates</Badge>
              </div>

              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-3 text-purple-600">API Access</h3>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li>• <strong>Basic API:</strong> Free tier available</li>
                  <li>• <strong>Enterprise API:</strong> Custom pricing</li>
                  <li>• <strong>White-label:</strong> Contact sales</li>
                  <li>• <strong>Priority Support:</strong> Paid plans</li>
                </ul>
                <Badge variant="outline" className="mt-2 text-xs">Scalable</Badge>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold">Third-Party Processing Fees</h3>
              
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">Payment Method</th>
                      <th className="text-left py-2">Processing Fee</th>
                      <th className="text-left py-2">Speed</th>
                      <th className="text-left py-2">Provider</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr className="border-b">
                      <td className="py-2">Credit/Debit Card</td>
                      <td className="py-2">2.9% + $0.30</td>
                      <td className="py-2">Instant</td>
                      <td className="py-2">Stripe</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2">PayPal</td>
                      <td className="py-2">2.5%</td>
                      <td className="py-2">Instant</td>
                      <td className="py-2">PayPal</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2">Bank Transfer (ACH)</td>
                      <td className="py-2">Free</td>
                      <td className="py-2">1-3 days</td>
                      <td className="py-2">Plaid</td>
                    </tr>
                    <tr>
                      <td className="py-2">Bitcoin</td>
                      <td className="py-2">Network fees</td>
                      <td className="py-2">Variable</td>
                      <td className="py-2">Blockchain</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">Fee Changes</h3>
              <p className="text-gray-700 text-sm">
                We may change our platform fees with 30 days' notice. Third-party processing fees are set by 
                our financial partners and subject to their terms. We will notify you of any material fee changes.
              </p>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* 8. Merchant Services */}
      <section id="merchant-services" className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl flex items-center space-x-2">
              <Building2 className="w-5 h-5" />
              <span>8. Merchant Services</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            
            <div className="space-y-4">
              <h3 className="font-semibold">Business Account Features</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ul className="text-gray-700 text-sm space-y-2">
                  <li>• Business profile and verification</li>
                  <li>• Payment acceptance and QR codes</li>
                  <li>• Invoice creation and management</li>
                  <li>• Transaction reporting and analytics</li>
                </ul>
                <ul className="text-gray-700 text-sm space-y-2">
                  <li>• Multi-location support</li>
                  <li>• API integration capabilities</li>
                  <li>• Customer communication tools</li>
                  <li>• Compliance and tax reporting</li>
                </ul>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-medium text-green-900 mb-2">Merchant Responsibilities</h3>
              <ul className="text-green-800 text-sm space-y-1">
                <li>• Maintain valid business licenses and registrations</li>
                <li>• Provide accurate business and tax information</li>
                <li>• Comply with applicable consumer protection laws</li>
                <li>• Honor refund and return policies</li>
                <li>• Maintain appropriate business insurance</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* 9. Prohibited Activities */}
      <section id="prohibited-activities" className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl flex items-center space-x-2">
              <Ban className="w-5 h-5 text-red-600" />
              <span>9. Prohibited Activities</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-medium text-red-900 mb-2">Strictly Prohibited</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ul className="text-red-800 text-sm space-y-1">
                  <li>• Money laundering or terrorist financing</li>
                  <li>• Fraudulent or deceptive practices</li>
                  <li>• Illegal gambling or gaming activities</li>
                  <li>• Sale of illegal goods or services</li>
                  <li>• Adult entertainment services</li>
                </ul>
                <ul className="text-red-800 text-sm space-y-1">
                  <li>• Pyramid schemes or multi-level marketing</li>
                  <li>• Unauthorized investment or lending services</li>
                  <li>• Identity theft or impersonation</li>
                  <li>• Circumventing payment processor rules</li>
                  <li>• Violating intellectual property rights</li>
                </ul>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="font-semibold">Platform Abuse</h3>
              <ul className="text-gray-700 text-sm space-y-1">
                <li>• Creating multiple accounts to circumvent limits</li>
                <li>• Automated or high-frequency token generation</li>
                <li>• Reverse engineering or tampering with our systems</li>
                <li>• Sharing account credentials or token codes publicly</li>
                <li>• Using the platform for political campaigning without permission</li>
              </ul>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h3 className="font-medium text-orange-900 mb-2">Consequences</h3>
              <p className="text-orange-800 text-sm">
                Violations may result in immediate account suspension, transaction reversal, reporting to 
                authorities, and potential legal action. We reserve the right to refuse service to anyone.
              </p>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* 10. Compliance & Legal */}
      <section id="compliance" className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl flex items-center space-x-2">
              <Scale className="w-5 h-5" />
              <span>10. Compliance & Regulatory</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            
            <div className="space-y-4">
              <h3 className="font-semibold">Regulatory Compliance</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-2">Financial Regulations</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Anti-Money Laundering (AML)</li>
                    <li>• Know Your Customer (KYC)</li>
                    <li>• Bank Secrecy Act (BSA)</li>
                    <li>• Consumer protection laws</li>
                  </ul>
                </div>
                
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-2">Data Protection</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• GDPR compliance</li>
                    <li>• CCPA compliance</li>
                    <li>• PCI DSS standards</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-2">Your Compliance Obligations</h3>
              <ul className="text-blue-800 text-sm space-y-1">
                <li>• Provide accurate information for identity verification</li>
                <li>• Report suspicious activities</li>
                <li>• Comply with applicable tax obligations</li>
                <li>• Maintain records as required by law</li>
                <li>• Cooperate with regulatory investigations</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* 11. Limitations & Disclaimers */}
      <section id="limitations" className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">11. Limitations & Disclaimers</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            
            <div className="space-y-4">
              <div className="border-l-4 border-red-500 pl-4">
                <h3 className="font-semibold mb-2">Service Availability</h3>
                <p className="text-gray-700 text-sm">
                  Our platform is provided "as is" without warranties. We do not guarantee uninterrupted service 
                  and may suspend operations for maintenance, security, or regulatory reasons.
                </p>
              </div>
              
              <div className="border-l-4 border-orange-500 pl-4">
                <h3 className="font-semibold mb-2">Third-Party Dependencies</h3>
                <p className="text-gray-700 text-sm">
                  We are not responsible for the performance, availability, or terms of our financial partners. 
                  Transaction processing depends entirely on third-party services.
                </p>
              </div>
              
              <div className="border-l-4 border-yellow-500 pl-4">
                <h3 className="font-semibold mb-2">Limitation of Liability</h3>
                <p className="text-gray-700 text-sm">
                  Our liability is limited to the technology service fees you have paid. We are not liable for 
                  consequential, indirect, or punitive damages arising from your use of the platform.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">Risk Acknowledgment</h3>
              <p className="text-gray-700 text-sm">
                Digital payment systems involve inherent risks including technology failures, security breaches, 
                and regulatory changes. You acknowledge and accept these risks when using our platform.
              </p>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* 12. Contact Information */}
      <section id="contact" className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl flex items-center space-x-2">
              <Mail className="w-5 h-5" />
              <span>12. Contact Information</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium mb-3">General Support</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4 text-gray-600" />
                    <a href="mailto:support@vaiu.io" className="text-blue-600 hover:text-blue-800">
                      support@vaiu.io
                    </a>
                  </div>
                  <div className="flex items-center space-x-2">
                    <FileText className="w-4 h-4 text-gray-600" />
                    <Link to="/help" className="text-blue-600 hover:text-blue-800">
                      Help Center
                    </Link>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium mb-3">Legal & Compliance</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4 text-gray-600" />
                    <a href="mailto:legal@vaiu.io" className="text-blue-600 hover:text-blue-800">
                      legal@vaiu.io
                    </a>
                  </div>
                  <div className="flex items-center space-x-2">
                    <User className="w-4 h-4 text-gray-600" />
                    <a href="mailto:compliance@vaiu.io" className="text-blue-600 hover:text-blue-800">
                      compliance@vaiu.io
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium mb-3">Company Information</h3>
              <div className="space-y-1 text-sm text-gray-700">
                <p>Vaiu Global Inc.</p>
                <p>8 The Green #19779</p>
                <p>Dover, DE 19901</p>
                <p>United States</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      <Separator className="my-8" />

      {/* Footer */}
      <div className="text-center text-gray-600 text-sm space-y-4">
        <div>
          <p className="font-medium mb-2">Related Documents</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/privacy-policy" className="text-blue-600 hover:text-blue-800">Privacy Policy</Link>
            <Link to="/cookie-policy" className="text-blue-600 hover:text-blue-800">Cookie Policy</Link>
            <Link to="/help" className="text-blue-600 hover:text-blue-800">Help Center</Link>
            <Link to="/contact" className="text-blue-600 hover:text-blue-800">Contact Us</Link>
          </div>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-xs">
            These Terms of Service constitute a legally binding agreement between you and Vaiu Global Inc. 
            By using our platform, you acknowledge that you have read, understood, and agree to be bound by these terms.
          </p>
        </div>
        
        <p className="text-xs">
          © 2025 Vaiu Global Inc. All rights reserved. | Document Version 1.0 | Effective {effectiveDate}
        </p>
      </div>
    </div>
  );
};

export default TermsOfServicePage; 